import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch, shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import uuidV4 from 'lib/string/uuidV4Utils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { transformUserCartItemsToCheckoutItems } from 'lib/checkout/userCart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { getOffersById } from 'api/offer'
import { reportLog } from 'services/userMonitoring'
import { fetchCharterBundleFlightItems, getCharterBundleStay, resolveCharterBundle, type CharterBundleHydrationResult, type CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { useShouldWaiveLuxPlusJoiningFee } from 'hooks/UserCart/useCartItems'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getRenewalOffer } from 'selectors/luxPlusSelectors'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { isExperienceItem, isFlightItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { getMultiCityGroupId, getMultiCityLegOrder } from '../utils/multiCityFlightHelpers'
import type { CartPanelFlightItem, CartPanelItem, CheckoutReadyItem } from './cartPanelItemTypes'
import {
  getCartPanelExperienceTimesKey,
  isCartPanelItemCheckoutReady,
  prepareCartPanelExperienceCheckoutItem,
} from '../utils/cartPanelUtils'
import { refreshTripChatBedbankItemSessions } from '../utils/tripChatCheckoutBedbankUtils'
import { createAccommodationV2SessionsForCartItems } from 'lib/checkout/createAccommodationV2SessionsForCartItems'
import { getCheckoutMultiCityGroups, hasLiveFlightSearch, refreshTripChatMultiCityFlights } from '../utils/tripChatCheckoutFlightUtils'
import { isActiveFlightAvailabilityBlocked } from '../utils/flightCartPanelUtils'
import { getTripPlannerCandidateCartItem } from '../utils/tripPlannerCartItemsUtils'
import { FlightViewTypes } from 'constants/flight'
import type { ItineraryContextValue } from '../contexts/ItineraryContext'
import { selectCancellationOptionBucket } from 'selectors/cancellationOptionsSelectors'

interface UseTripChatCheckoutActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
  reconcileCartForCheckout: ItineraryContextValue['reconcileCartForCheckout']
}

interface UseTripChatCheckoutActionsReturn {
  checkoutCartItems: (items: Array<CartPanelItem>) => Promise<void>
  prepareCheckoutCartItems: (items: Array<CartPanelItem>) => void
  isCheckingOut: boolean
}

interface PreparedFlights {
  inputKey: string
  cartItems: Map<string, App.MultiCartItem>
  preparedAt: number
}

interface PendingPreparedFlights {
  inputKey: string
  promise: Promise<PreparedFlights>
}

type FreshCheckoutPreparation =
  | { status: 'ready'; items: Array<CheckoutReadyItem> }
  | { status: 'items_changed'; items: Array<CartPanelItem> }
  | { status: 'reconcile_failed'; error: unknown }
  | { status: 'chat_updating' }
  | { status: 'cart_refetch_failed'; error: unknown }
  | { status: 'flight_refresh_failed'; error: unknown }

type FailedCheckoutPreparation = Exclude<FreshCheckoutPreparation, { status: 'ready' }>

const PREPARED_FLIGHTS_MAX_AGE_MS = 2 * 60 * 1000

function isCheckoutReadyItem(item: CartPanelItem): item is CheckoutReadyItem {
  return isCartPanelItemCheckoutReady(item) && !!item.cartItem
}

function expandCartPanelItems(items: Array<CartPanelItem>): Array<CartPanelItem> {
  return items.flatMap(item =>
    item.productType === 'flight' && item.multiCityLegs ? item.multiCityLegs : [item],
  )
}

function getCheckoutReadyItems(items: Array<CartPanelItem>): Array<CheckoutReadyItem> {
  return expandCartPanelItems(items.filter(isCartPanelItemCheckoutReady)).filter(isCheckoutReadyItem)
}

function hasCheckoutReadyItems(item: CartPanelItem): boolean {
  return getCheckoutReadyItems([item]).length > 0
}

// Reconcile can only rebuild hotel and experience rows, so a flight would only add a way for checkout to fail.
function getReconcilableTripItemIds(items: Array<CheckoutReadyItem>): Array<number> {
  return items.flatMap(item => (item.productType === 'flight' ? [] : [item.tripItemId]))
}

function withFreshCartItemSnapshots(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  const freshItem = withFreshCartItemSnapshot(item, cartItems)
  if (freshItem.productType !== 'flight' || !freshItem.multiCityLegs) return freshItem

  return {
    ...freshItem,
    multiCityLegs: freshItem.multiCityLegs.map(leg => withFreshFlightCartItemSnapshot(leg, cartItems)),
  }
}

function isPreparedFlightsUsable(
  prepared: PreparedFlights | undefined,
  inputKey: string,
): prepared is PreparedFlights {
  return !!prepared &&
    prepared.inputKey === inputKey &&
    Date.now() - prepared.preparedAt < PREPARED_FLIGHTS_MAX_AGE_MS &&
    [...prepared.cartItems.values()].every(({ item }) => hasLiveFlightSearch(item))
}

function withFreshFlightCartItemSnapshot(
  item: CartPanelFlightItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelFlightItem {
  if (!item.cartItemId) return item
  const freshCartItem = cartItems.get(item.cartItemId)
  const flightCartItem = freshCartItem && isMultiCartFlightItem(freshCartItem) ? freshCartItem : undefined
  // Availability comes from the cart row, so it is re-derived from the fresh one.
  const isAvailabilityBlocked = isActiveFlightAvailabilityBlocked(flightCartItem)
  return {
    ...item,
    cartItem: flightCartItem,
    flightCartItem,
    isAvailabilityBlocked,
    pricing: { ...item.pricing, isAvailable: !isAvailabilityBlocked },
  }
}

function withFreshCartItemSnapshot(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  if (item.productType === 'flight') return withFreshFlightCartItemSnapshot(item, cartItems)
  return { ...item, cartItem: getTripPlannerCandidateCartItem(cartItems, item.cartSelection) }
}

function toCheckoutItem(
  item: CheckoutReadyItem,
  currency: string,
  experiences: App.State['experience']['experiences'],
  experienceTimes: App.State['experience']['experienceTimes'],
): App.Checkout.AnyItem {
  const checkoutItem = {
    ...item.cartItem.item,
    itemId: item.cartItem.item.itemId ?? item.cartItem.id,
    transactionKey: item.cartItem.item.transactionKey ?? uuidV4(),
  }
  if (item.productType !== 'experience' || !isExperienceItem(checkoutItem)) return checkoutItem

  const key = getCartPanelExperienceTimesKey(checkoutItem, currency)
  return prepareCartPanelExperienceCheckoutItem(
    checkoutItem,
    experiences[checkoutItem.experienceId],
    experienceTimes[checkoutItem.experienceId]?.[key]?.slots,
  )
}

function combineMultiCityGroup(legCheckoutItems: Array<App.Checkout.AnyItem>): Array<App.Checkout.AnyItem> {
  const flightItems = legCheckoutItems.filter(isFlightItem)
  const flights = flightItems.flatMap(checkoutItem => checkoutItem.flights)
  const [firstCheckoutItem] = flightItems
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  // A leg may carry several segments (a connection); every leg must be a flight
  // item, but the combined itinerary keeps all segments in order.
  if (!firstCheckoutItem || !firstFlight || !lastFlight || flightItems.length !== legCheckoutItems.length) {
    return legCheckoutItems
  }

  return [{
    ...firstCheckoutItem,
    fareType: 'multiCity',
    viewType: FlightViewTypes.MULTI_CITY,
    originAirportCode: firstFlight.departingAirportCode,
    destinationAirportCode: lastFlight.arrivalAirportCode,
    hasFinalFare: false,
    fareFetchError: undefined,
    // Re-derived from every leg's flights; leg one's standalone total would
    // undercharge the group until the final fare is fetched.
    totalFare: undefined,
    flights,
  }]
}

function combineMultiCityCheckoutItems(
  activeItems: Array<CheckoutReadyItem>,
  checkoutItems: Array<App.Checkout.AnyItem>,
): Array<App.Checkout.AnyItem> {
  const groupByLegKey = new Map<string, Array<CheckoutReadyItem>>()
  getCheckoutMultiCityGroups(activeItems).forEach(group => group.forEach(leg => groupByLegKey.set(leg.key, group)))
  if (groupByLegKey.size === 0) return checkoutItems

  const checkoutItemByKey = new Map(activeItems.map((item, index) => [item.key, checkoutItems[index]]))
  const combinedGroups = new Set<Array<CheckoutReadyItem>>()
  return activeItems.flatMap((item, index) => {
    const group = groupByLegKey.get(item.key)
    if (!group) return [checkoutItems[index]]
    if (combinedGroups.has(group)) return []
    combinedGroups.add(group)
    const legCheckoutItems = [...group]
      .sort((a, b) => getMultiCityLegOrder(a) - getMultiCityLegOrder(b))
      .flatMap(leg => checkoutItemByKey.get(leg.key) ?? [])
    return combineMultiCityGroup(legCheckoutItems)
  })
}

export function useTripChatCheckoutActions({
  chatId,
  multiCartId,
  refetchCartItems,
  reconcileCartForCheckout,
}: UseTripChatCheckoutActionsArgs): UseTripChatCheckoutActionsReturn {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const shouldWaive = useShouldWaiveLuxPlusJoiningFee()
  const checkoutInFlightRef = useRef(false)
  const preparedFlightsRef = useRef<PreparedFlights | undefined>(undefined)
  const pendingPreparedFlightsRef = useRef<PendingPreparedFlights | undefined>(undefined)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const { subscriptionOffers, renewalSubscriptionOffer, experiences, experienceTimes, email, cancellationOptionBucket } = useAppSelector(state => ({
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    renewalSubscriptionOffer: getRenewalOffer(state).offer,
    experiences: state.experience.experiences,
    experienceTimes: state.experience.experienceTimes,
    email: state.auth?.account?.email,
    cancellationOptionBucket: selectCancellationOptionBucket(state),
  }), shallowEqual)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const enableSabreContent = useFeatureFlag(OptimizelyFeatureFlags.flightsEnableSabreContent)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  const flightRefreshErrorMessage = intl.formatMessage({
    id: 'J1dfTHgb7DOLNSkugHPz',
    defaultMessage: 'This flight is no longer available. Please search for new flights.',
    description: 'Error shown when a multi-city flight group cannot be re-priced at checkout',
  })

  const charterBundleErrorMessage = intl.formatMessage({
    id: 'oS-wuNEBq6t8u5nGT-2z',
    defaultMessage: 'The included flight isn’t available for your selected dates. Please adjust your stay and try again.',
    description: 'Error shown when the bundled charter flight cannot be added at checkout',
  })

  const chatUpdatingMessage = intl.formatMessage({
    id: 'ccd9N0rkEe3sUNHcn9zt',
    defaultMessage: 'TripChat is still updating. Please try again in a moment.',
    description: 'Warning shown when Trip Chat checkout waits for a change to the trip or its cart to finish',
  })

  const emptyCheckoutMessage = intl.formatMessage({
    id: 'NrlaeuBIm_kkUCkneM7W',
    defaultMessage: 'No items are ready to book. Please review your cart and try again.',
    description: 'Warning shown when refreshing Trip Chat checkout finds no remaining items ready to book',
  })

  const prepareFlights = useCallback(async(activeItems: Array<CheckoutReadyItem>): Promise<Array<CheckoutReadyItem>> => {
    const flightItems = getCheckoutMultiCityGroups(activeItems).flat()
    if (flightItems.length === 0) return activeItems

    // Cache only flight searches. Cart selection and readiness are rebuilt on
    // every checkout click, even while a matching flight search is in flight.
    const deps = {
      currentCurrency: currency,
      currentRegionCode,
      email,
      enableSabreContent,
      requestLuxPlusPricing: canRequestLuxPlusFlightPricing,
    }
    const inputKey = JSON.stringify({
      chatId,
      multiCartId,
      ...deps,
      flights: flightItems.map(item => ({
        key: item.key,
        cartItemId: item.cartItemId,
        groupId: getMultiCityGroupId(item),
        legOrder: getMultiCityLegOrder(item),
        cartItem: item.cartItem,
      })),
    })
    let prepared = preparedFlightsRef.current
    if (!isPreparedFlightsUsable(prepared, inputKey)) {
      let pending = pendingPreparedFlightsRef.current
      if (pending?.inputKey !== inputKey) {
        const promise = refreshTripChatMultiCityFlights(flightItems, deps).then(items => ({
          inputKey,
          cartItems: new Map(items.map(item => [item.cartItem.id, item.cartItem])),
          preparedAt: Date.now(),
        }))
        pending = { inputKey, promise }
        pendingPreparedFlightsRef.current = pending
      }
      try {
        prepared = await pending.promise
        if (pendingPreparedFlightsRef.current === pending) preparedFlightsRef.current = prepared
      } finally {
        if (pendingPreparedFlightsRef.current === pending) pendingPreparedFlightsRef.current = undefined
      }
    }
    const preparedCartItems = prepared.cartItems
    return activeItems.map(item => {
      const cartItem = preparedCartItems.get(item.cartItem.id)
      if (item.productType !== 'flight' || !cartItem || !isMultiCartFlightItem(cartItem)) return item
      return { ...item, cartItem, flightCartItem: cartItem }
    })
  }, [chatId, multiCartId, currency, currentRegionCode, email, enableSabreContent, canRequestLuxPlusFlightPricing])

  const prepareFreshCheckoutItems = useCallback(async(
    chatIdToReconcile: number,
    multiCartIdToCheckout: string,
    items: Array<CartPanelItem>,
  ): Promise<FreshCheckoutPreparation> => {
    const tripItemIds = getReconcilableTripItemIds(getCheckoutReadyItems(items))
    if (tripItemIds.length > 0) {
      const reconciliation = await reconcileCartForCheckout(chatIdToReconcile, multiCartIdToCheckout, tripItemIds)
      if (reconciliation.status === 'chat_updating') return { status: 'chat_updating' }
      if (reconciliation.status === 'failed') return { status: 'reconcile_failed', error: reconciliation.error }
    }

    let freshCartItems: Map<string, App.MultiCartItem>
    try {
      freshCartItems = await refetchCartItems()
    } catch(error) {
      return { status: 'cart_refetch_failed', error }
    }

    const refreshed = items.map(item => ({ item, freshItem: withFreshCartItemSnapshots(item, freshCartItems) }))
    const changedItems = refreshed
      .filter(({ item, freshItem }) => hasCheckoutReadyItems(item) && !hasCheckoutReadyItems(freshItem))
      .map(({ item }) => item)
    if (changedItems.length > 0) return { status: 'items_changed', items: changedItems }

    const activeItems = getCheckoutReadyItems(refreshed.map(({ freshItem }) => freshItem))
    try {
      return { status: 'ready', items: await prepareFlights(activeItems) }
    } catch(error) {
      return { status: 'flight_refresh_failed', error }
    }
  }, [reconcileCartForCheckout, refetchCartItems, prepareFlights])

  const notifyPreparationFailure = useCallback((
    chatIdToCheckout: number,
    multiCartIdToCheckout: string,
    preparation: FailedCheckoutPreparation,
  ) => {
    const logContext = { chatId: chatIdToCheckout, multiCartId: multiCartIdToCheckout }
    switch (preparation.status) {
      case 'chat_updating':
        showSnackbar(chatUpdatingMessage, 'warning')
        return
      case 'reconcile_failed':
        reportLog('Trip chat checkout cart reconciliation failed', { ...logContext, error: preparation.error }, 'warn')
        showSnackbar(defaultErrorMessage, 'critical')
        return
      case 'cart_refetch_failed':
        reportLog('Trip chat checkout cart refetch failed', { ...logContext, error: preparation.error }, 'warn')
        showSnackbar(defaultErrorMessage, 'critical')
        return
      case 'items_changed':
        // Booking the rest would quietly leave these out of the trip.
        reportLog('Trip chat checkout stopped for items no longer ready', {
          ...logContext,
          tripItemIds: preparation.items.map(item => item.tripItemId),
        }, 'warn')
        showSnackbar(intl.formatMessage({
          id: 'Cd8chkfoHg8Sk2XSZLEp',
          defaultMessage: '{itemNames} {count, plural, one {is} other {are}} no longer ready to book. Please check your cart and try again.',
          description: 'Warning shown when Trip Chat checkout stops because some cart items stopped being ready to book, naming those items',
        }, {
          itemNames: intl.formatList(preparation.items.map(item => item.name), { type: 'conjunction' }),
          count: preparation.items.length,
        }), 'warning')
        return
      case 'flight_refresh_failed':
        reportLog('Trip chat checkout flight refresh failed', { ...logContext, error: preparation.error }, 'warn')
        showSnackbar(flightRefreshErrorMessage, 'critical')
        return
      default:
        assertUnreachable(preparation)
    }
  }, [intl, chatUpdatingMessage, defaultErrorMessage, flightRefreshErrorMessage])

  const prepareCheckoutCartItems = useCallback((items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId) return
    const activeItems = getCheckoutReadyItems(items)
    prepareFlights(activeItems).catch(() => undefined)
  }, [chatId, multiCartId, prepareFlights])

  const checkoutCartItems = useCallback(async(items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    setIsCheckingOut(true)

    try {
      const preparation = await prepareFreshCheckoutItems(chatId, multiCartId, items)
      if (preparation.status !== 'ready') {
        notifyPreparationFailure(chatId, multiCartId, preparation)
        return
      }
      const activeItems = preparation.items
      if (activeItems.length === 0) {
        showSnackbar(emptyCheckoutMessage, 'warning')
        return
      }

      getCartMachineActor()?.send({ type: 'set active cart', cartId: multiCartId })

      const checkoutSource: App.CheckoutSource = {
        type: 'trip_planner',
        multiCartId,
        externalReferenceId: String(chatId),
      }
      const perActiveItemCheckoutItems = activeItems.map(item => toCheckoutItem(item, currency, experiences, experienceTimes))
      const checkoutItems = combineMultiCityCheckoutItems(activeItems, perActiveItemCheckoutItems)

      // Charter flight bundles (Maldives MEL↔MLE direct): detect each charter
      // bundle from the live offer (shared helper; requireFlightsEnabled is false
      // at checkout because getOffersById doesn't reliably populate it) and derive
      // its charter (+ feeder) flight legs, then inject them so checkout opens with
      // the bundled flight. Batch the offer fetch so we don't fire one request per
      // hotel on the checkout-click path.
      const accommodationOfferIds = [...new Set(
        activeItems.flatMap(item =>
          item.productType === 'accommodation' && item.offerId ? [item.offerId] : [],
        ),
      )]
      const offersById = new Map<string, App.AnyOffer>()
      if (accommodationOfferIds.length > 0) {
        try {
          const offers = await getOffersById(accommodationOfferIds, currentRegionCode)
          for (const offer of offers) {
            if ('id' in offer) offersById.set(offer.id, offer)
          }
        } catch { /* per-item missing-offer guard below decides whether to block */ }
      }

      const charterBundleItems: Array<{
        offer: App.HotelOffer | App.HotelOfferSummary
        charterFlightBundle: CharterFlightBundleSelection
        occupancies: Array<App.Occupants>
        checkIn: string
        checkOut: string
        checkoutItem: App.Checkout.InstantBookingLEHotelItem
      }> = []
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i]
        if (item.productType !== 'accommodation' || !item.offerId) continue
        const offer = offersById.get(item.offerId)
        const checkoutItem = perActiveItemCheckoutItems[i]
        const instantBookingItem = isInstantBookingLEHotelItem(checkoutItem) ? checkoutItem : undefined
        // Missing offer: getOffersById dropped this one (it can return a subset),
        // so we can't inspect it to rule out a charter bundle. Fail closed only for
        // items that could actually carry a charter flight - a persisted selection,
        // or an instant-booking LE hotel (the only item type charter bundles ride
        // on). Unrelated accommodation (bedbank/SPP/villa, which getOffersById may
        // not even return) checks out normally instead of blocking the whole cart.
        if (!offer) {
          if (isMaldivesCharterEnabled && (item.charterFlightBundle || instantBookingItem)) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        const resolved = resolveCharterBundle({
          offer,
          isMaldivesCharterEnabled,
          persistedBundle: item.charterFlightBundle,
          occupancies: getCharterBundleStay(checkoutItem, {
            checkIn: item.checkIn,
            checkOut: item.checkOut,
            occupancies: item.occupancies,
          }).occupancies,
          requireFlightsEnabled: false,
        })
        if (!resolved) {
          if (item.charterFlightBundle) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        // The charter legs must ride on an instant-booking LE hotel item; if a
        // detected bundle isn't one we can't build its flight, so block checkout
        // rather than silently opening it flight-less.
        if (!instantBookingItem) {
          showSnackbar(charterBundleErrorMessage, 'critical')
          return
        }
        charterBundleItems.push({
          offer: resolved.offer,
          charterFlightBundle: resolved.charterFlightBundle,
          occupancies: resolved.occupancies,
          checkIn: instantBookingItem.checkIn,
          checkOut: instantBookingItem.checkOut,
          checkoutItem: instantBookingItem,
        })
      }

      // Hydrate every charter bundle's flight legs in parallel (one search per hotel).
      let charterResults: Array<CharterBundleHydrationResult>
      try {
        charterResults = await Promise.all(charterBundleItems.map(({ offer, charterFlightBundle, occupancies, checkIn, checkOut, checkoutItem }) =>
          fetchCharterBundleFlightItems({
            offer,
            charterFlightBundle,
            accommodationItems: [checkoutItem],
            checkIn,
            checkOut,
            occupancies,
            region: currentRegionCode,
            currency,
            email,
          }),
        ))
      } catch(error) {
        reportLog('Trip chat charter bundle hydration threw', {
          error,
          chatId,
          multiCartId,
          offerIds: charterBundleItems.map(({ offer }) => offer.id),
        }, 'error')
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      const charterFlightItems: Array<App.Checkout.FlightItem> = []
      let hydrationFailed = false
      for (let i = 0; i < charterResults.length; i++) {
        const result = charterResults[i]
        if (!result.ok) {
          hydrationFailed = true
          const { offer, charterFlightBundle, checkIn, checkOut } = charterBundleItems[i]
          reportLog('Trip chat charter bundle hydration failed', {
            reason: result.reason,
            offerId: offer.id,
            charterBundleRoute: charterFlightBundle.charterBundleRoute,
            originAirportCode: charterFlightBundle.originAirportCode,
            fareClass: charterFlightBundle.fareClass,
            checkIn,
            checkOut,
            chatId,
            multiCartId,
          }, 'error')
          continue
        }
        charterFlightItems.push(...result.flightItems)
      }
      if (hydrationFailed) {
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      let transformed = transformUserCartItemsToCheckoutItems(checkoutItems, subscriptionOffers, shouldWaive, renewalSubscriptionOffer, cancellationOptionBucket)
      try {
        transformed = await refreshTripChatBedbankItemSessions(transformed, dispatch)
        transformed = await createAccommodationV2SessionsForCartItems(transformed, dispatch)
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const allItems = [...transformed, ...charterFlightItems]
      dispatch(initialiseCheckout(allItems.map(item => ({ ...item, transactionKey: uuidV4() })), { checkoutSource }, true))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } finally {
      checkoutInFlightRef.current = false
      setIsCheckingOut(false)
    }
  }, [chatId, multiCartId, prepareFreshCheckoutItems, notifyPreparationFailure, emptyCheckoutMessage, charterBundleErrorMessage, defaultErrorMessage, isMaldivesCharterEnabled, experiences, experienceTimes, subscriptionOffers, renewalSubscriptionOffer, shouldWaive, dispatch, currentRegionCode, currency, email, cancellationOptionBucket])

  return { checkoutCartItems, prepareCheckoutCartItems, isCheckingOut }
}
